import React, { useState } from 'react';
import styled from 'styled-components';
import classnames from 'classnames';
import type { HTMLAttributes } from 'react';
import type { AllColorType, AllIconType, ElementColorType, SizeType } from '../../../util/types';
import type { StudentType } from '../../types';
import type { StudentButtonTextType } from '../../../global/buttons/StudentButton';
import { Alert, Slide } from '../../../components';
import ValueHeadline from './ValueHeadline';
import { StudentButton } from '../../../global';
import { getPolishedColors } from '../../../util/styled';

type Ref = HTMLDivElement;
type HTMLProps = HTMLAttributes<Ref>;
interface Props extends HTMLProps {
  /**
   * Students list data.
   */
  data: StudentType[];
  /**
   * Section icon.
   */
  icon?: AllIconType;
  iconSize?: number | SizeType;
  iconColor?: AllColorType;
  /**
   * Section title.
   */
  title?: string;
  showColon?: boolean;
  /**
   * Alt
   */
  unit?: string;
  /**
   * Section more info.
   */
  moreInfo?: string | null;
  /**
   * Section color.
   */
  color: ElementColorType;
  /**
   * Toggle button text.
   */
  buttonText: StudentButtonTextType | string;
  /**
   * Disable inner container.
   */
  disableContainer?: boolean;
  /**
   * Extra section content.
   */
  children?: React.ReactNode | null;
  /**
   * Whether a single student is focused
   */
  focused?: boolean;
}

const StudentsDisplay = ({
  data,
  icon,
  iconSize,
  iconColor,
  title,
  showColon,
  unit,
  moreInfo = null,
  color,
  buttonText,
  disableContainer = false,
  children = null,
  focused = false,
  ...other
}: Props): JSX.Element => {

  const [ showInfo, setShowInfo ] = useState(false);
  const [ showContent, setShowContent ] = useState(false);

  return (
    <DisplayContainerStyled {...other}>
      <div className={'flex items-center justify-between'}>
        <ValueHeadline
          icon={icon}
          iconSize={iconSize}
          iconColor={iconColor}
          value={focused ? null : data.length}
          title={title}
          showColon={showColon}
          unit={unit}
          onMoreInfo={() => setShowInfo(!showInfo)}
        />
        <StudentButton
          disabled={data.length <= 0}
          color={color}
          text={buttonText}
          toggled={showContent}
          onClick={() => setShowContent(!showContent)}
        />
      </div>
      {/* Toggled info */}
      {null != moreInfo && (
        <Alert disableIcon show={showInfo} className={'my-md'}>
          {moreInfo}
        </Alert>
      )}
      {/* Component content */}
      <Slide when={null != children && showContent}>
        <div className={'bg-white'}>
          <StudentsContainerStyled
            $color={color}
            $disableContainer={disableContainer}
            className={`${classnames({ 'container' : !disableContainer })} mt-md`}
          >
            {children ?? ''}
          </StudentsContainerStyled>
        </div>
      </Slide>
    </DisplayContainerStyled>
  );
};

const DisplayContainerStyled = styled('div')`
  &:last-child {
    margin: 0 !important;
  }
`;

const StudentsContainerStyled = styled('div')<{ $color: ElementColorType; $disableContainer: boolean }>`
  ${(props) => !props.$disableContainer && `border-radius: ${props.theme.radius.default};`}
  border: ${(props) => !props.$disableContainer ? '2px' : '0'} solid ${(props) => getPolishedColors(props).main.color}};  
`;

StudentsDisplay.displayName = 'StudentsDisplay';

export default StudentsDisplay;
